import dayjs from "dayjs";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import { buildAcquisitionAnalyticsParams } from "../acquisition/acquisitionSourceFilter";
import { coerceUserAnalyticsNumber } from "./userAnalyticsCoerce";
import type {
  UserAnalyticsDailyHttpBody,
  UserAnalyticsDailyPoint,
  UserAnalyticsDailyQueryParams,
  UserAnalyticsDailyResult,
} from "./userAnalyticsDailyTypes";

/**
 * Daily users API contract (GET …/users/analytics/kpis/daily):
 * - Query: `start_date`, `end_date` only. Do not send `metrics[in]=signups,ftds`
 *   (comma-joined) — the backend returns validation_error with invalid
 *   `["signups,ftds"]`; use repeated params or omit the filter.
 * - When `metrics[in]` is omitted, defaults are `signups` and `ftds`
 *   (`meta.filters.default_values` / `selected_values` on the response).
 * - Response rows may include `ftd_amount` even though the chart plots only
 *   signups and ftds; XLSX export includes all three fields from each point.
 */
const ISO_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Builds every YYYY-MM-DD date between `start` and `end` (inclusive). Returns
 * an empty array if either bound is missing or the range is inverted.
 */
function buildDateRange(start: string, end: string): string[] {
  if (!start || !end) return [];
  const startDay = dayjs(start, ISO_DATE_FORMAT, true);
  const endDay = dayjs(end, ISO_DATE_FORMAT, true);
  if (!startDay.isValid() || !endDay.isValid()) return [];
  if (endDay.isBefore(startDay, "day")) return [];

  const out: string[] = [];
  let cursor = startDay;
  while (!cursor.isAfter(endDay, "day")) {
    out.push(cursor.format(ISO_DATE_FORMAT));
    cursor = cursor.add(1, "day");
  }
  return out;
}

/**
 * Parses the daily-breakdown HTTP envelope and *densifies* the result so the
 * chart x-axis covers every day in [start, end], even days the backend omits
 * because they had zero activity. Coerces numeric strings / non-numerics to 0.
 */
export function parseUserAnalyticsDailyResponse(
  body: unknown,
  params: UserAnalyticsDailyQueryParams,
): UserAnalyticsDailyResult {
  const b = body as UserAnalyticsDailyHttpBody | null | undefined;
  const rawList = Array.isArray(b?.data) ? b!.data : [];

  // Index by ISO date so the dense fill below is O(n + range).
  const byDate = new Map<string, UserAnalyticsDailyPoint>();
  for (const row of rawList) {
    const date = typeof row?.date === "string" ? row.date : "";
    if (!date) continue;
    byDate.set(date, {
      date,
      signups: coerceUserAnalyticsNumber(row.signups),
      ftds: coerceUserAnalyticsNumber(row.ftds),
      ftd_amount: coerceUserAnalyticsNumber(row.ftd_amount),
    });
  }

  const denseDates = buildDateRange(params.start_date, params.end_date);
  // If the range is unparseable, fall back to whatever the backend gave us so
  // we don't drop data on the floor.
  const points: UserAnalyticsDailyPoint[] =
    denseDates.length > 0
      ? denseDates.map(
          (date) =>
            byDate.get(date) ?? {
              date,
              signups: 0,
              ftds: 0,
              ftd_amount: 0,
            },
        )
      : Array.from(byDate.values());

  return {
    points,
    filters: Array.isArray(b?.meta?.filters) ? b!.meta!.filters! : [],
  };
}

/**
 * Fetches the daily breakdown for a single date range.
 *
 * We intentionally omit `metrics[in]` from the query string. The backend
 * rejects a comma-joined value (`signups,ftds`) as a single invalid metric;
 * it expects repeated params or no filter at all. When omitted, the API
 * defaults to `signups` and `ftds` (see `meta.filters.default_values` on the
 * daily endpoint). Rows may still include `ftd_amount`; the chart only plots
 * signups/ftds, but exports include ftd_amount when present in the payload.
 */
export async function fetchUserAnalyticsDaily(
  params: UserAnalyticsDailyQueryParams,
): Promise<UserAnalyticsDailyResult> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_USER_ANALYTICS_DAILY, {
    params: buildAcquisitionAnalyticsParams(params),
  });
  return parseUserAnalyticsDailyResponse(response?.data, params);
}
